/**
 * BerichtConversation — herbruikbare thread-weergave (reacties + reageren +
 * afhandelen) voor één bericht. Gebruikt door BerichtenInline (WI/WO-pagina's)
 * en BerichtenPage. Field-zijde: eigen reacties rechts, kantoor links.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  BerichtError,
  fetchBerichtDetail,
  refreshFotoUrl,
  replyBericht,
  resolveBericht,
  uploadBerichtFoto,
  type BerichtDetail,
} from '../../lib/berichten'

interface Props {
  berichtId: string
  onChanged?: () => void
}

export function BerichtConversation({ berichtId, onChanged }: Props) {
  const [detail, setDetail] = useState<BerichtDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reply, setReply] = useState('')
  const [fotoPaths, setFotoPaths] = useState<string[]>([])
  const [fotoPreviews, setFotoPreviews] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [posting, setPosting] = useState(false)
  const [resolving, setResolving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setDetail(await fetchBerichtDetail(berichtId))
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Detail laden mislukt')
    } finally {
      setLoading(false)
    }
  }, [berichtId])

  useEffect(() => {
    load()
  }, [load])

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        const result = await uploadBerichtFoto(file, berichtId)
        setFotoPaths((p) => [...p, result.path])
        setFotoPreviews((p) => [...p, result.url])
      }
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Foto-upload mislukt')
    } finally {
      setUploading(false)
    }
  }

  async function handleReply() {
    if (!reply.trim()) return
    setPosting(true)
    try {
      await replyBericht({ berichtId, body: reply.trim(), fotoUrls: fotoPaths })
      setReply('')
      setFotoPaths([])
      setFotoPreviews([])
      await load()
      onChanged?.()
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Reactie mislukt')
    } finally {
      setPosting(false)
    }
  }

  async function handleResolve() {
    setResolving(true)
    try {
      await resolveBericht(berichtId)
      await load()
      onChanged?.()
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Afhandelen mislukt')
    } finally {
      setResolving(false)
    }
  }

  if (loading && !detail) {
    return <div className="px-2 py-2 text-xs text-kleda-text-muted border-t">Laden…</div>
  }
  if (!detail) {
    return <div className="px-2 py-2 text-xs text-red-700 border-t">{error || 'Detail niet beschikbaar'}</div>
  }

  return (
    <div className="border-t border-kleda-border">
      <ul className="px-2 py-2 space-y-2 max-h-64 overflow-y-auto bg-kleda-bg-clean">
        {detail.reacties.map((r) => {
          const isField = r.richting === 'field_to_office'
          const isSysteem = r.bron === 'Systeem'
          return (
            <li
              key={r.id}
              className={isSysteem ? 'flex justify-center' : isField ? 'flex justify-end' : 'flex justify-start'}
            >
              <div
                className={
                  isSysteem
                    ? 'bg-gray-200 text-gray-700 text-[11px] italic rounded px-2 py-1'
                    : isField
                      ? 'bg-kleda-primary text-white rounded-lg px-2 py-1.5 text-xs max-w-[85%]'
                      : 'bg-white border border-kleda-border text-kleda-text-main rounded-lg px-2 py-1.5 text-xs max-w-[85%]'
                }
              >
                <p className="whitespace-pre-wrap break-words">{r.body}</p>
                {r.fotoUrls.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.fotoUrls.map((p) => (
                      <FotoThumb key={p} path={p} />
                    ))}
                  </div>
                )}
                {!isSysteem && (
                  <div className={`mt-0.5 text-[10px] ${isField ? 'text-white/70' : 'text-kleda-text-muted'}`}>
                    {r.auteurNaam || r.auteurEmail}
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      {detail.status !== 'afgehandeld' ? (
        <div className="border-t border-kleda-border px-2 py-2 space-y-1.5">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={2}
            placeholder="Reactie aan kantoor…"
            className="w-full text-xs border border-kleda-border rounded px-2 py-1.5"
          />
          {fotoPreviews.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {fotoPreviews.map((url, idx) => (
                <div key={fotoPaths[idx]} className="relative">
                  <img src={url} alt="" className="w-10 h-10 object-cover rounded border border-kleda-border" />
                  <button
                    type="button"
                    onClick={() => {
                      setFotoPaths((p) => p.filter((_, i) => i !== idx))
                      setFotoPreviews((p) => p.filter((_, i) => i !== idx))
                    }}
                    className="absolute -top-1 -right-1 bg-white border border-kleda-border rounded-full w-4 h-4 text-[10px] leading-none"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <label className={`inline-flex items-center gap-1 text-xs cursor-pointer text-kleda-text-muted ${uploading ? 'opacity-50' : ''}`}>
              📷 {uploading ? 'Uploaden…' : 'Foto'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                disabled={uploading}
              />
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleResolve}
                disabled={resolving}
                className="text-xs text-green-700 font-medium px-2 py-1"
              >
                {resolving ? '…' : 'Afgehandeld ✓'}
              </button>
              <button
                type="button"
                onClick={handleReply}
                disabled={posting || uploading || !reply.trim()}
                className="bg-kleda-primary text-white text-xs font-medium px-3 py-1.5 rounded disabled:bg-gray-300"
              >
                {posting ? '…' : 'Reageer'}
              </button>
            </div>
          </div>
          {error && <p className="text-[11px] text-red-700">{error}</p>}
        </div>
      ) : (
        <div className="px-2 py-2 border-t border-kleda-border text-[11px] text-kleda-text-muted italic">
          Bericht is afgehandeld.
        </div>
      )}
    </div>
  )
}

// ─── Foto thumb (with signed-URL refresh) ────────────────────────────────────

export function FotoThumb({ path }: { path: string }) {
  const [src, setSrc] = useState<string | null>(path.startsWith('http') ? path : null)
  const [errored, setErrored] = useState(false)

  useEffect(() => {
    if (path.startsWith('http')) return
    let cancelled = false
    refreshFotoUrl(path)
      .then((url) => {
        if (!cancelled) setSrc(url)
      })
      .catch(() => {
        if (!cancelled) setErrored(true)
      })
    return () => {
      cancelled = true
    }
  }, [path])

  if (errored) {
    return (
      <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">foto?</span>
    )
  }
  if (!src) {
    return <span className="inline-block w-10 h-10 bg-gray-200 rounded animate-pulse" />
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer">
      <img src={src} alt="" className="w-10 h-10 object-cover rounded border border-kleda-border" />
    </a>
  )
}
